import type { ActionExecutor } from "../TriggerEngine.interfaces";

export const switchScene: ActionExecutor = async (
  _radio,

  config,

  { emitAction, suppressEvent },
) => {
  const sceneId = config?.scene;

  if (!sceneId) return;

  suppressEvent("scene_switched", 3000);

  emitAction("switch_scene", { sceneId });
};

// Transfer: moves resource from source to destination, clears source

export const toggleArea: ActionExecutor = async (
  radio,

  config,

  { emitAction },
) => {
  const { fromScene, fromArea, toScene, toArea } = config;

  if (!fromScene || !fromArea || !toScene || !toArea) return;

  const { openDatabase, saveSnapshotDatabase } = await import("../../Utils/db");

  const db = await openDatabase();

  const scenes = await db.scenes.toArray();

  const srcScene = scenes.find((s) => s.id === fromScene);

  const dstScene = scenes.find((s) => s.id === toScene);

  if (!srcScene || !dstScene) return;

  const srcArea = srcScene.model.areas.find((a) => a.id === fromArea);

  const dstArea = dstScene.model.areas.find((a) => a.id === toArea);

  if (!srcArea || !dstArea) return;

  dstArea.media.resource = srcArea.media.resource;

  // Remove the area from the source scene

  srcScene.model.areas = srcScene.model.areas.filter((a) => a.id !== fromArea);

  await db.scenes.update(srcScene.id, { model: srcScene.model });

  if (srcScene.id !== dstScene.id) {
    await db.scenes.update(dstScene.id, { model: dstScene.model });
  }

  const currentScene = scenes.find((s) => s.active);

  if (
    currentScene &&
    (currentScene.id === srcScene.id || currentScene.id === dstScene.id)
  ) {
    await radio.updateTemplate(currentScene.model);

    emitAction("studio_refreshed", { model: currentScene.model });
  }

  await saveSnapshotDatabase();
};

// Clone: copies resource from source to destination, keeps source intact

export const cloneArea: ActionExecutor = async (
  radio,

  config,

  { emitAction },
) => {
  const { fromScene, fromArea, toScene, toArea } = config;

  if (!fromScene || !fromArea || !toScene || !toArea) return;

  const { openDatabase, saveSnapshotDatabase } = await import("../../Utils/db");

  const db = await openDatabase();

  const scenes = await db.scenes.toArray();

  const srcScene = scenes.find((s) => s.id === fromScene);

  const dstScene = scenes.find((s) => s.id === toScene);

  if (!srcScene || !dstScene) return;

  const srcArea = srcScene.model.areas.find((a) => a.id === fromArea);

  const dstArea = dstScene.model.areas.find((a) => a.id === toArea);

  if (!srcArea || !dstArea) return;

  dstArea.media.resource = JSON.parse(JSON.stringify(srcArea.media.resource));

  await db.scenes.update(dstScene.id, { model: dstScene.model });

  const currentScene = scenes.find((s) => s.active);

  if (currentScene && currentScene.id === dstScene.id) {
    await radio.updateTemplate(currentScene.model);

    emitAction("studio_refreshed", { model: currentScene.model });
  }

  await saveSnapshotDatabase();
};

export const hideArea: ActionExecutor = async (
  radio,

  config,

  { emitAction },
) => {
  const { scene: sceneId, areaId } = config;

  if (!sceneId || !areaId) return;

  const { openDatabase, saveSnapshotDatabase } = await import("../../Utils/db");

  const db = await openDatabase();

  const scenes = await db.scenes.toArray();

  const scene = scenes.find((s) => s.id === sceneId);

  if (!scene) return;

  const area = scene.model.areas.find((a) => a.id === areaId);

  if (!area) return;

  area.editorState = {
    ...area.editorState,

    isHidden: true,
  };

  await db.scenes.update(scene.id, { model: scene.model });

  const currentScene = scenes.find((s) => s.active);

  if (currentScene && currentScene.id === scene.id) {
    await radio.updateTemplate(currentScene.model);

    emitAction("studio_refreshed", { model: currentScene.model });
  }

  await saveSnapshotDatabase();
};

export const showArea: ActionExecutor = async (
  radio,

  config,

  { emitAction },
) => {
  const { scene: sceneId, areaId } = config;

  if (!sceneId || !areaId) return;

  const { openDatabase, saveSnapshotDatabase } = await import("../../Utils/db");

  const db = await openDatabase();

  const scenes = await db.scenes.toArray();

  const scene = scenes.find((s) => s.id === sceneId);

  if (!scene) return;

  const area = scene.model.areas.find((a) => a.id === areaId);

  if (!area) return;

  area.editorState = {
    ...area.editorState,

    isHidden: false,
  };

  await db.scenes.update(scene.id, { model: scene.model });

  const currentScene = scenes.find((s) => s.active);

  if (currentScene && currentScene.id === scene.id) {
    await radio.updateTemplate(currentScene.model);

    emitAction("studio_refreshed", { model: currentScene.model });
  }

  await saveSnapshotDatabase();
};

const toggleSceneItem = async (
  radio: Parameters<ActionExecutor>[0],

  config: Record<string, string>,

  emitAction: (action: string, payload: Record<string, unknown>) => void,

  collection: "widgets" | "elements",

  active: boolean,
) => {
  const { scene: sceneId, itemId } = config;

  if (!sceneId || !itemId) return;

  const { openDatabase, saveSnapshotDatabase } = await import("../../Utils/db");

  const db = await openDatabase();

  const scenes = await db.scenes.toArray();

  const scene = scenes.find((s) => s.id === sceneId);

  if (!scene) return;

  const item = scene.model[collection]?.find((i) => i.id === itemId);

  if (!item) return;

  item.active = active;

  await db.scenes.update(scene.id, { model: scene.model });

  const currentScene = scenes.find((s) => s.active);

  if (currentScene && currentScene.id === scene.id) {
    await radio.updateTemplate(currentScene.model);

    emitAction("studio_refreshed", { model: currentScene.model });
  }

  await saveSnapshotDatabase();
};

export const hideWidget: ActionExecutor = async (
  radio,

  config,

  { emitAction },
) => toggleSceneItem(radio, config, emitAction, "widgets", false);

export const showWidget: ActionExecutor = async (
  radio,

  config,

  { emitAction },
) => toggleSceneItem(radio, config, emitAction, "widgets", true);

export const hideElement: ActionExecutor = async (
  radio,

  config,

  { emitAction },
) => toggleSceneItem(radio, config, emitAction, "elements", false);

export const showElement: ActionExecutor = async (
  radio,

  config,

  { emitAction },
) => toggleSceneItem(radio, config, emitAction, "elements", true);
